import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Clock,
  LoaderCircle,
  Newspaper
} from 'lucide-react';

import NotFoundPage from '@/features/NotFound/NotFoundPage';
import Footer from '@/shared/components/Footer';
import Navbar from '@/shared/components/Navbar';
import { Button } from '@/shared/components/ui/Button';
import { useI18n } from '@/shared/contexts/I18nProvider';
import { getAbsoluteUrl, usePageSeo } from '@/shared/hooks';

import BlogArticleFooter from './BlogArticleFooter';
import { getBlogPostPath } from './blogPosts';
import { useBlogPost, useBlogPosts } from './useBlogContent';

const createLocalDate = (dateString: string) => {
  const [year = 0, month = 1, day = 1] = dateString.split('-').map(Number);

  return new Date(year, month - 1, day);
};

const getAuthorInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'DS';

const blockHtmlTagPattern =
  /^\s*<(address|article|aside|blockquote|div|dl|figure|h[1-6]|hr|ol|p|pre|section|table|ul)\b/i;

const articleHtmlClassName = [
  '[&_a]:text-accent-primary',
  '[&_a]:underline',
  '[&_a]:underline-offset-4',
  '[&_blockquote]:border-l-2',
  '[&_blockquote]:border-accent-primary/45',
  '[&_blockquote]:pl-4',
  '[&_blockquote]:italic',
  '[&_code]:rounded',
  '[&_code]:bg-background-tertiary',
  '[&_code]:px-1.5',
  '[&_code]:py-0.5',
  '[&_code]:font-mono',
  '[&_em]:text-text-primary',
  '[&_h2]:text-3xl',
  '[&_h2]:font-light',
  '[&_h2]:mt-10',
  '[&_h2]:tracking-tight',
  '[&_h2]:text-text-primary',
  '[&_h2:first-child]:mt-0',
  '[&_h3]:text-2xl',
  '[&_h3]:font-light',
  '[&_h3]:mt-8',
  '[&_h3]:tracking-tight',
  '[&_h3]:text-text-primary',
  '[&_img]:my-8',
  '[&_img]:w-full',
  '[&_img]:rounded-lg',
  '[&_img]:border',
  '[&_img]:border-border-primary',
  '[&_li]:mt-2',
  '[&_ol]:ml-6',
  '[&_ol]:list-decimal',
  '[&_p]:mt-5',
  '[&_p:first-child]:mt-0',
  '[&_strong]:font-semibold',
  '[&_strong]:text-text-primary',
  '[&_ul]:ml-6',
  '[&_ul]:list-disc'
].join(' ');

const allowedArticleTags = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'EM',
  'FIGCAPTION',
  'FIGURE',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'PRE',
  'S',
  'SPAN',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL'
]);

const allowedArticleAttributes = new Set([
  'alt',
  'colspan',
  'height',
  'href',
  'loading',
  'rel',
  'rowspan',
  'src',
  'target',
  'title',
  'width'
]);

const isSafeArticleUrl = (value: string, attribute: 'href' | 'src') => {
  const normalizedValue = value.trim().toLowerCase();

  if (
    normalizedValue.startsWith('#') ||
    normalizedValue.startsWith('/') ||
    normalizedValue.startsWith('./') ||
    normalizedValue.startsWith('../')
  ) {
    return true;
  }

  if (
    attribute === 'src' &&
    /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(value)
  ) {
    return true;
  }

  return attribute === 'href'
    ? /^(https?:|mailto:|tel:)/i.test(value)
    : /^https?:/i.test(value);
};

const sanitizeArticleHtml = (html: string) => {
  const document = new DOMParser().parseFromString(html, 'text/html');

  document.body
    .querySelectorAll(
      'script, style, iframe, object, embed, link, meta, form, input, button, textarea, select, svg, math'
    )
    .forEach(element => element.remove());

  Array.from(document.body.querySelectorAll('*')).forEach(element => {
    if (!allowedArticleTags.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach(attribute => {
      const attributeName = attribute.name.toLowerCase();

      if (!allowedArticleAttributes.has(attributeName)) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (
        (attributeName === 'href' || attributeName === 'src') &&
        !isSafeArticleUrl(attribute.value, attributeName)
      ) {
        element.removeAttribute(attribute.name);
      }
    });

    if (
      element.tagName === 'A' &&
      element.getAttribute('target') === '_blank'
    ) {
      element.setAttribute('rel', 'noopener noreferrer');
    }

    if (element.tagName === 'IMG') {
      element.setAttribute('loading', 'lazy');
    }
  });

  return document.body.innerHTML;
};

type ArticleHtmlBlockProps = {
  html: string;
};

const ArticleHtmlBlock = ({ html }: ArticleHtmlBlockProps) => {
  const safeHtml = sanitizeArticleHtml(html);

  if (blockHtmlTagPattern.test(safeHtml)) {
    return (
      <div
        className={articleHtmlClassName}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  return (
    <p
      className={articleHtmlClassName}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { content, language, locale, localizedPath } = useI18n();
  const {
    error: blogPostError,
    isLoading: isBlogPostLoading,
    isNotFound,
    post
  } = useBlogPost(slug, language);
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
    [locale]
  );
  const { posts: allPosts } = useBlogPosts(language);
  const relatedPosts = useMemo(() => {
    if (!post) {
      return [];
    }

    const candidates = allPosts.filter(
      candidate => candidate.slug !== post.slug
    );
    const matchingPosts = candidates.filter(
      candidate =>
        candidate.serviceKey === post.serviceKey ||
        candidate.industryKey === post.industryKey
    );
    const otherPosts = candidates.filter(
      candidate => !matchingPosts.some(match => match.slug === candidate.slug)
    );

    return [...matchingPosts, ...otherPosts].slice(0, 3);
  }, [allPosts, post]);
  const typeLabelByValue = useMemo(
    () =>
      new Map(
        content.insights.filters.typeOptions.map(option => [
          option.value,
          option.label
        ])
      ),
    [content.insights.filters.typeOptions]
  );
  const defaultSeo = useMemo(
    () => ({
      description: content.meta.description,
      imageAlt: content.meta.imageAlt,
      keywords: content.meta.keywords,
      title: content.meta.title,
      twitterDescription: content.meta.twitterDescription
    }),
    [content.meta]
  );
  const seoConfig = useMemo(() => {
    if (!post || post.sections.length === 0) {
      return null;
    }

    const canonicalPath = post.canonicalUrl || post.canonicalPath;
    const canonicalUrl = getAbsoluteUrl(canonicalPath);
    const imageUrl = getAbsoluteUrl(post.imageSrc);
    const articleType = post.kind === 'news' ? 'NewsArticle' : 'BlogPosting';

    return {
      articleAuthor: post.authorName,
      articleSection: post.category,
      articleTags: post.seoKeywords,
      canonicalPath,
      description: post.seoDescription,
      imageAlt: post.imageAlt,
      imageHeight: '935',
      imagePath: post.imageSrc,
      imageType: 'image/jpeg',
      imageWidth: '1800',
      keywords: post.seoKeywords,
      locale,
      modifiedAt: post.updatedAt ?? post.publishedAt,
      ogType: 'article' as const,
      publishedAt: post.publishedAt,
      siteName: content.common.dataSliv,
      title: post.seoTitle,
      twitterData1: post.readTime,
      twitterLabel1: content.blog.readTime,
      structuredData: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': articleType,
            '@id': `${canonicalUrl}#article`,
            author: {
              '@type': 'Organization',
              name: post.authorName
            },
            dateModified: post.updatedAt ?? post.publishedAt,
            datePublished: post.publishedAt,
            description: post.seoDescription,
            headline: post.title,
            image: imageUrl,
            inLanguage: locale,
            keywords: post.seoKeywords,
            mainEntityOfPage: {
              '@id': canonicalUrl,
              '@type': 'WebPage'
            },
            publisher: {
              '@type': 'Organization',
              logo: {
                '@type': 'ImageObject',
                url: getAbsoluteUrl('/images/logo-transparent.png')
              },
              name: content.common.dataSliv
            }
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                item: getAbsoluteUrl('/'),
                name: content.blog.breadcrumbHome,
                position: 1
              },
              {
                '@type': 'ListItem',
                item: getAbsoluteUrl('/insights'),
                name: content.blog.breadcrumbInsights,
                position: 2
              },
              {
                '@type': 'ListItem',
                item: canonicalUrl,
                name: post.title,
                position: 3
              }
            ]
          }
        ]
      }
    };
  }, [
    content.blog.breadcrumbHome,
    content.blog.breadcrumbInsights,
    content.blog.readTime,
    content.common.dataSliv,
    locale,
    post
  ]);

  usePageSeo(seoConfig, defaultSeo);

  if (isBlogPostLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background-primary text-text-primary antialiased">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-40">
          <div
            className="flex items-center gap-3 font-mono text-sm uppercase tracking-[0.14em] text-text-secondary"
            role="status"
          >
            <LoaderCircle
              className="h-5 w-5 animate-spin text-accent-primary"
              aria-hidden="true"
            />
            {content.blog.loadingArticle}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (blogPostError && !post) {
    return (
      <div className="flex min-h-screen flex-col bg-background-primary text-text-primary antialiased">
        <Navbar />
        <main className="mx-auto flex w-full max-w-3xl flex-1 items-center px-4 py-40 sm:px-6">
          <div className="w-full rounded-lg border border-border-primary bg-background-card p-8 text-center shadow-sm shadow-shadow">
            <BookOpen
              className="mx-auto h-8 w-8 text-accent-primary"
              aria-hidden="true"
            />
            <h1 className="mt-4 text-3xl font-light text-text-primary">
              {content.insights.emptyFeedTitle}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
              {content.insights.emptyFeedDescription}
            </p>
            <Button asChild variant="outline" className="mt-6 gap-2">
              <Link to={localizedPath('/insights')}>
                <ArrowLeft className="h-4 w-4" />
                {content.blog.backToInsights}
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (isNotFound || !post || post.sections.length === 0) {
    return <NotFoundPage />;
  }

  const PostKindIcon = post.kind === 'news' ? Newspaper : BookOpen;
  const kindLabel = typeLabelByValue.get(post.kind) ?? post.kind;

  return (
    <div className="min-h-screen bg-background-primary text-text-primary antialiased">
      <Navbar />

      <main>
        <article
          className="border-b border-border-muted bg-background-primary"
          itemScope
          itemType={
            post.kind === 'news'
              ? 'https://schema.org/NewsArticle'
              : 'https://schema.org/BlogPosting'
          }
        >
          <header className="relative overflow-hidden border-b border-border-muted">
            <img
              src={post.imageSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-[0.16] dark:opacity-25"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-background-primary/88" />

            <div className="relative z-10 mx-auto max-w-5xl px-4 pb-12 pt-36 sm:px-6 sm:pb-16 sm:pt-40 lg:px-8">
              <nav
                aria-label="Breadcrumb"
                className="mb-8 flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-text-tertiary"
              >
                <Link
                  to={localizedPath('/')}
                  className="hover:text-text-primary"
                >
                  {content.blog.breadcrumbHome}
                </Link>
                <span>/</span>
                <Link
                  to={localizedPath('/insights')}
                  className="hover:text-text-primary"
                >
                  {content.blog.breadcrumbInsights}
                </Link>
              </nav>

              <div className="mb-5 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent-primary/30 bg-background-card/70 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-accent-primary shadow-lg shadow-shadow backdrop-blur">
                  <PostKindIcon className="h-4 w-4" />
                  {kindLabel}
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary">
                  {post.service}
                </span>
                <span className="text-text-muted" aria-hidden="true">
                  •
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary">
                  {post.industry}
                </span>
              </div>

              <h1
                className="max-w-5xl text-[clamp(2.25rem,5vw,4.9rem)] font-light leading-[1.02] tracking-tight text-text-primary"
                itemProp="headline"
              >
                {post.title}
              </h1>
              <p
                className="mt-6 max-w-3xl text-lg leading-8 text-text-secondary sm:text-xl"
                itemProp="description"
              >
                {post.excerpt}
              </p>

              <div className="mt-8 flex flex-col gap-4 border-y border-border-muted py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-accent-primary/30 bg-accent-primary/10 font-mono text-sm font-semibold text-accent-primary"
                    aria-hidden="true"
                  >
                    {getAuthorInitials(post.authorName)}
                  </span>
                  <span>
                    <span
                      className="block text-sm font-semibold text-text-primary"
                      itemProp="author"
                    >
                      {post.authorName}
                    </span>
                    <span className="mt-0.5 block text-xs text-text-tertiary">
                      {post.authorRole || content.blog.authorRoleFallback}
                    </span>
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs uppercase tracking-[0.12em] text-text-tertiary">
                  <time
                    className="flex items-center gap-2"
                    dateTime={post.publishedAt}
                    itemProp="datePublished"
                  >
                    <CalendarDays className="h-4 w-4 text-accent-primary" />
                    {dateFormatter.format(createLocalDate(post.publishedAt))}
                  </time>
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-accent-primary" />
                    {post.readTime}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[minmax(0,1fr)_17rem] lg:px-8">
            <div className="min-w-0">
              <figure className="mb-10">
                <img
                  src={post.imageSrc}
                  alt={post.imageAlt}
                  className="aspect-[16/8] w-full rounded-lg border border-border-primary bg-background-card object-cover shadow-sm shadow-shadow"
                  itemProp="image"
                />
                {post.imageCaption && (
                  <figcaption className="mt-3 text-center text-sm italic text-text-tertiary">
                    {post.imageCaption}
                  </figcaption>
                )}
              </figure>

              <div className="space-y-10">
                {post.sections.map((section, index) => (
                  <section key={`${section.heading}-${index}`}>
                    {section.heading && (
                      <h2 className="text-3xl font-light tracking-tight text-text-primary">
                        {section.heading}
                      </h2>
                    )}
                    <div
                      className={[
                        'space-y-5 text-lg leading-8 text-text-secondary',
                        section.heading ? 'mt-4' : ''
                      ].join(' ')}
                    >
                      {section.paragraphs.map(paragraph => (
                        <ArticleHtmlBlock key={paragraph} html={paragraph} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {post.articleImages && post.articleImages.length > 0 && (
                <div className="mt-12 space-y-8">
                  {post.articleImages.map((image, index) => (
                    <figure key={image.id}>
                      <img
                        src={image.src}
                        alt={
                          image.alt ||
                          `${content.blog.articleImage} ${index + 1}`
                        }
                        className="w-full rounded-lg border border-border-primary bg-background-card object-cover shadow-sm shadow-shadow"
                        loading="lazy"
                      />
                      {image.caption && (
                        <figcaption className="mt-3 text-center text-sm italic text-text-tertiary">
                          {image.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              )}

              <BlogArticleFooter post={post} />
            </div>

            <aside className="h-fit rounded-lg border border-border-primary bg-background-card p-5 shadow-sm shadow-shadow lg:sticky lg:top-[calc(var(--app-navbar-height,96px)+1rem)]">
              <dl className="grid gap-4">
                <div>
                  <dt className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                    {content.blog.service}
                  </dt>
                  <dd className="mt-1 text-text-primary">{post.service}</dd>
                </div>
                <div>
                  <dt className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                    {content.blog.industry}
                  </dt>
                  <dd className="mt-1 text-text-primary">{post.industry}</dd>
                </div>
                {post.updatedAt && post.updatedAt !== post.publishedAt && (
                  <div>
                    <dt className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                      {content.blog.updated}
                    </dt>
                    <dd className="mt-1 text-text-primary">
                      <time dateTime={post.updatedAt} itemProp="dateModified">
                        {dateFormatter.format(createLocalDate(post.updatedAt))}
                      </time>
                    </dd>
                  </div>
                )}
              </dl>

              <Button asChild variant="outline" className="mt-6 w-full gap-2">
                <Link to={localizedPath('/insights')}>
                  <ArrowLeft className="h-4 w-4" />
                  {content.blog.backToInsights}
                </Link>
              </Button>
            </aside>
          </div>
        </article>

        <section className="bg-background-secondary py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="font-mono text-sm uppercase tracking-[0.2em] text-accent-primary">
                {content.blog.continueReading}
              </p>
              <h2 className="mt-3 text-3xl font-light tracking-tight text-text-primary sm:text-4xl">
                {content.blog.relatedTitle}
              </h2>
              <p className="mt-4 text-base leading-7 text-text-secondary">
                {content.blog.relatedDescription}
              </p>
            </div>

            {relatedPosts.length > 0 ? (
              <div className="mt-8 grid gap-5 lg:grid-cols-3">
                {relatedPosts.map(relatedPost => {
                  const RelatedIcon =
                    relatedPost.kind === 'news' ? Newspaper : BookOpen;

                  return (
                    <Link
                      key={relatedPost.slug}
                      to={localizedPath(getBlogPostPath(relatedPost))}
                      className="group overflow-hidden rounded-lg border border-border-primary bg-background-card shadow-sm shadow-shadow transition-colors hover:border-accent-primary/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                    >
                      <img
                        src={relatedPost.imageSrc}
                        alt={relatedPost.imageAlt}
                        className="aspect-[16/10] w-full border-b border-border-muted object-cover"
                      />
                      <div className="p-5">
                        <div className="flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-text-tertiary">
                          <span className="inline-flex items-center gap-1.5 text-accent-primary">
                            <RelatedIcon className="h-3.5 w-3.5" />
                            {typeLabelByValue.get(relatedPost.kind) ??
                              relatedPost.kind}
                          </span>
                          <time dateTime={relatedPost.publishedAt}>
                            {dateFormatter.format(
                              createLocalDate(relatedPost.publishedAt)
                            )}
                          </time>
                        </div>
                        <h3 className="mt-3 text-xl font-medium leading-7 text-text-primary transition-colors group-hover:text-accent-primary">
                          {relatedPost.title}
                        </h3>
                        <p className="mt-3 text-sm leading-6 text-text-secondary">
                          {relatedPost.excerpt}
                        </p>
                        <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-accent-primary">
                          {content.blog.readArticle}
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="mt-8 flex flex-col gap-5 rounded-lg border border-border-primary bg-background-card p-6 shadow-sm shadow-shadow sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-2xl text-sm leading-6 text-text-secondary">
                  {content.insights.description}
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="w-fit shrink-0 gap-2"
                >
                  <Link to={localizedPath('/insights')}>
                    {content.blog.browseAllInsights}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
